import { setInvalid, setValid } from 'domain/feature/hotsite/Forms/validations'
import { IUserConfigForm } from '../../PersonalConfigForm/store/configForm'
import { IFormFieldProperties } from 'domain/feature/hotsite/Forms/contracts'
import { getCredLocalStorage } from 'sharedKernel/auth/credLocalStorage'
import { IAuthPermission } from 'sharedKernel/auth/authReducer'

const labels = {
  required: 'Este campo é obrigatório',
  invalidPhone: 'Número inválido',
  invalidZipcode: 'CEP inválido'
}

const logged = getCredLocalStorage();
const companyType = logged?.company?.type;
const isAdminCityHall = companyType?.type === "Admin City Hall";
const isCityHall = companyType?.type === "City Hall";

// Regex para telefone fixo (8 dígitos) ou celular (9 dígitos)
const phoneRegex = /^\(\d{2}\)\s?\d{4,5}-\d{4}$/
const zipcodeRegex: RegExp = /^\d{5}-\d{3}$/

const CITY_HALL_ONLY_FIELDS: Array<keyof IUserConfigForm> = [
  'cityHallName',
  'mayorName',
  'teamSize',
  'hasSecretary',
  'secretary',
  'secretaryResponsible'
]

const CAIXA_SPECIFIC_FIELDS: Array<keyof IUserConfigForm> = [
  'caixaRole',
  'caixaDepartment',
  'caixaEducation',
  'caixaInstitution',
  'caixaMatricula'
]

const COMPANY_PROFILE_REQUIRED_FIELDS: Array<keyof IUserConfigForm> = [
  'name',
  'contactName',
  'phone',
  'zipcode',
  'lineOne',
  'number',
  'city',
  'state',
  'country',
  'neighborhood'
]

export const isResolverUser = (permissions: IAuthPermission | undefined): boolean =>
  permissions?.features?.some(f => f.code === 'idea') ?? false

export function handleUserConfigFormValidity(
  form: IUserConfigForm,
  isResolver: boolean
): IUserConfigForm {
  let isCaixaEmployee = false
  try {
    isCaixaEmployee = !!(form.caixaRole?.value && form.caixaDepartment?.value)
  } catch {
    isCaixaEmployee = false
  }
  
  // 1) Limpa erros de todos os campos (exceto isFormValid)
  ;(Object.keys(form) as Array<keyof IUserConfigForm>)
  .filter(key => key !== 'isFormValid')
  .forEach(key => {
    const field = form[key] as IFormFieldProperties | undefined
    if (field && field.isRequired !== undefined) {
      setValid(field)
    }
  })
    
  const isEmpty = (value: any): boolean => {
    if (value == null || value === undefined) return true
    if (typeof value === 'string') {
      return value.trim() === ''
    }
    if (typeof value === 'number') {
      return false 
    }
    if (typeof value === 'boolean') {
      return false 
    }
    return value === ''
  }

  // ===== Validação específica para City Hall =====
  if (isAdminCityHall || isCityHall) {
    const hasSecretary =
      form.hasSecretary?.value === 'true'

    const requiredFields = isAdminCityHall
      ? [
          'name',
          'email',
          'cityHallName',
          'mayorName',
          'teamSize',
          'hasSecretary',
          ...(hasSecretary
            ? ['secretary', 'secretaryResponsible']
            : [])
        ]
      : ['name', 'email']

    // Valida apenas os campos exigidos
    requiredFields.forEach(fieldName => {
      const field = form[fieldName as keyof IUserConfigForm] as IFormFieldProperties
      if (field && field.isRequired && isEmpty(field.value)) {
        setInvalid(field, labels.required)
      }
    })

    // Marca como válidos os campos que não participam da validação
    ;(Object.keys(form) as Array<keyof IUserConfigForm>)
      .filter(key => key !== 'isFormValid')
      .filter(key => !requiredFields.includes(key as string))
      .forEach(key => {
        const field = form[key] as IFormFieldProperties
        if (field) {
          setValid(field)
        }
      })

    // Recalcula validade do formulário
    form.isFormValid = !requiredFields.some(fieldName => {
      const field = form[fieldName as keyof IUserConfigForm] as IFormFieldProperties
      return field?.isInvalid
    })

    return form
  }

  // 2) Validação genérica de obrigatoriedade
  if (isCaixaEmployee) {
    const caixaRequiredFields = ['name', 'email', 'phone', 'caixaRole', 'caixaDepartment', 'caixaMatricula']
    
    caixaRequiredFields.forEach(fieldName => {
      const field = form[fieldName as keyof IUserConfigForm] as IFormFieldProperties
      if (field && field.isRequired) {
        if (isEmpty(field.value)) {
          setInvalid(field, labels.required)
        }
      }
    })
    
    const startupFields = ['activity', 'target', 'businessModel', 'employees', 'technologies', 
                          'pitch', 'developmentStage', 'annualRevenue', 'hasRaisedInvestment',
                          'investmentAmount', 'investmentOrigin', 'investmentOtherOrigin',
                          'zipcode', 'lineOne', 'number', 'city', 'state', 'neighborhood']
    
    startupFields.forEach(fieldName => {
      const field = form[fieldName as keyof IUserConfigForm] as IFormFieldProperties
      if (field) {
        setValid(field) 
      }
    })
  } else if (isResolver) {
    ;(Object.keys(form) as Array<keyof IUserConfigForm>)
      .filter(key => key !== 'isFormValid')
      .filter(key => !CAIXA_SPECIFIC_FIELDS.includes(key))
      .filter(key => !CITY_HALL_ONLY_FIELDS.includes(key))
      .forEach(key => {
        const field = form[key] as IFormFieldProperties
        if (field?.isRequired && isEmpty(field.value)) {
          setInvalid(field, labels.required)
        }
      })
    CAIXA_SPECIFIC_FIELDS.forEach(fieldName => {
      const field = form[fieldName] as IFormFieldProperties | undefined
      if (field) setValid(field)
    })
    CITY_HALL_ONLY_FIELDS.forEach(key => {
      const field = form[key] as IFormFieldProperties | undefined
      if (field) setValid(field)
    })
  } else {
    COMPANY_PROFILE_REQUIRED_FIELDS.forEach(fieldName => {
      const field = form[fieldName] as IFormFieldProperties | undefined
      if (field?.isRequired && isEmpty(field.value)) {
        setInvalid(field, labels.required)
      }
    })

    ;(Object.keys(form) as Array<keyof IUserConfigForm>)
      .filter(key => key !== 'isFormValid')
      .filter(key => !COMPANY_PROFILE_REQUIRED_FIELDS.includes(key))
      .forEach(key => {
        const field = form[key] as IFormFieldProperties | undefined
        if (field) setValid(field)
      })
  }

  // 3) Validação de formato do telefone
  const phoneField = form.phone
  const phoneValue = phoneField?.value
  const phoneIsEmpty = isEmpty(phoneValue)
  
  if (isCaixaEmployee) {
    if (!phoneIsEmpty && phoneValue) {
      const phoneStr = String(phoneValue)
      if (!phoneRegex.test(phoneStr)) {
        setInvalid(phoneField, labels.invalidPhone)
      } else {
        setValid(phoneField)
      }
    } else if (phoneIsEmpty && phoneField?.isRequired) {
    } else if (phoneIsEmpty && !phoneField?.isRequired) {
      setValid(phoneField)
    }
  } else {
    if (phoneIsEmpty && phoneField?.isRequired) {
    } else if (!phoneIsEmpty || !phoneField?.isRequired) {
      setValid(phoneField)
    }
  }

  const zipcodeField = form.zipcode
  const zipcodeValue = zipcodeField?.value
  if (!isEmpty(zipcodeValue) && zipcodeValue != null) {
    if (!zipcodeRegex.test(cleanZipcode(String(zipcodeValue)))) {
      setInvalid(zipcodeField, labels.invalidZipcode)
    } else {
      setValid(zipcodeField)
    }
  }

  const invested = isResolver && form.hasRaisedInvestment?.value === 'true'
  if (invested) {
  // valida investmentAmount e investmentOrigin
    if (isEmpty(form.investmentAmount?.value)) {
      setInvalid(form.investmentAmount, 'Informe o valor captado')
    }
    if (isEmpty(form.investmentOrigin?.value)) {
      setInvalid(form.investmentOrigin, 'Informe a origem do investimento')
    }
    const originVal = form.investmentOrigin?.value
    const otherField = form.investmentOtherOrigin
    if (originVal === '' || originVal === 'Outro') {
      if (isEmpty(otherField?.value)) {
        setInvalid(otherField, labels.required)
      } else {
        setValid(otherField)
      }
    } else {
      setValid(otherField)
    }
  } else {
  // quando não captou investimento, limpa qualquer erro desses dois campos
    setValid(form.investmentAmount)
    setValid(form.investmentOrigin)
    setValid(form.investmentOtherOrigin)
  }

  // 6) Recalcula isFormValid
  const keys = Object.keys(form) as Array<keyof IUserConfigForm>
  const anyInvalid = keys
    .filter(key => key !== 'isFormValid')
    .some(key => {
      const f = form[key] as IFormFieldProperties
      return f && f.isRequired && f.isInvalid
    })
  form.isFormValid = !anyInvalid

  return form
}

export const isPhoneOrMobileFilled = (
  configForm: IUserConfigForm
): boolean => true

export const cleanCNPJ = (cnpj: string): string => {
  if (cnpj == null) return ''
  return cnpj.replace(/[^\d]/g, '')
}

export const cleanZipcode = (zipcode: string): string => {
  if (zipcode == null) return ''
  const digits = zipcode.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`
}
